import { useCallback, useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

/** Namespaced. This panel owns its hostname today, but a bare `admin-theme`
 *  key is the kind of thing that collides the day it does not. */
const KEY = "ewn.admin.theme";

function read(): Theme | null {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Private mode, or storage disabled. The system preference still works.
    return null;
  }
}

export function getInitialTheme(): Theme {
  return read() ?? (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light");
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/** Applied before React renders, so there is no flash of the wrong theme. */
export function initTheme(): void {
  applyTheme(getInitialTheme());
}

/**
 * ONE STORE, not one `useState` per call site.
 *
 * With per-call-site state the second caller never learns the first one flipped
 * it: the page goes dark while the toggle still shows a moon, and pressing it
 * then "sets" the value it already had and appears to do nothing. An external
 * store also means anything that has to REACT to the theme can subscribe here
 * rather than watch the <html> class list.
 */
let current: Theme | null = null;
const listeners = new Set<() => void>();

function snapshot(): Theme {
  if (current === null) current = getInitialTheme();
  return current;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function setTheme(theme: Theme): void {
  if (snapshot() === theme) return;
  current = theme;
  applyTheme(theme);
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* The choice still holds for this page load. */
  }
  listeners.forEach((fn) => fn());
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, snapshot, snapshot);
  const toggle = useCallback(() => {
    setTheme(snapshot() === "dark" ? "light" : "dark");
  }, []);
  return { theme, toggle };
}

/** Tests only: drop the cached value so a cleared localStorage is read again. */
export function resetThemeForTests(): void {
  current = null;
}
